import { Box, useTheme, Button, InputBase, IconButton, Switch, Tooltip, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import ModalCharge from "../../modal/modalCharge";
import ModalSucces from "../../modal/modalSucces";
import ModalError from "../../modal/modalError";
import CustomToolbar from "../../utils/CustomToolbar";
import ExcelJS from "exceljs";
import { getBancoView, updateBanco } from "../../services/banco.services";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const BancoView = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Modal states
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState("");

  // Local UI state for switches and dates
  const [workedMap, setWorkedMap] = useState({});
  const [pagarFechaMap, setPagarFechaMap] = useState({});

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Fetch data and initialize UI state
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const permisos = await getBancoView();
        setData(permisos);
        setFilteredData(permisos);

        // Initialize switch and date state from backend data
        const initialWorked = {};
        const initialPagarFecha = {};
        permisos.forEach((item) => {
          initialWorked[item.id] = !!item.pagar_fecha;
          initialPagarFecha[item.id] = item.pagar_fecha ? dayjs(item.pagar_fecha) : null;
        });
        setWorkedMap(initialWorked);
        setPagarFechaMap(initialPagarFecha);
      } catch (error) {
        setSaveErrorMessage("Error al cargar los datos");
        setSaveError(true);
        console.error("Error fetching permisos:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter logic
  useEffect(() => {
    if (!searchValue.trim()) {
      setFilteredData(data);
    } else {
      const filtered = data.filter((item) =>
        item.nombre.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredData(filtered);
    }
  }, [searchValue, data]);

  // Export logic (unchanged)
  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Permisos");

    worksheet.columns = [
      { header: "ID", key: "id", width: 10 },
      { header: "ID_USUARIO", key: "id_usuario", width: 15 },
      { header: "NOMBRE", key: "nombre", width: 30 },
      { header: "Fecha Deber", key: "deber_fecha", width: 15 },
      { header: "Fecha Pagar", key: "pagar_fecha", width: 15 },
    ];

    const exportableData = filteredData.map((row) => ({
      id: row.id,
      id_usuario: row.id_usuario,
      nombre: row.nombre,
      Deber_fecha: row.deber_fecha
        ? dayjs(row.deber_fecha).format("DD/MM/YYYY")
        : "",
      Pagar_fecha: row.pagar_fecha
        ? dayjs(row.pagar_fecha).format("DD/MM/YYYY")
        : "",
    }));

    worksheet.addRows(exportableData);

    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF143F04" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber !== 1) {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Permisos_${dayjs().format("YYYYMMDD")}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Switch logic (just update local state)
  const handleSwitchChange = (row, checked) => {
    setWorkedMap((prev) => ({ ...prev, [row.id]: checked }));
    setPagarFechaMap((prev) => ({ ...prev, [row.id]: checked ? dayjs() : null }));
  };

  // DatePicker logic (just update local state)
  const handleDateChange = (row, date) => {
    setPagarFechaMap((prev) => ({ ...prev, [row.id]: date }));
  };

  // Save all changes to backend
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updates = Object.keys(workedMap).map((id) => ({
        id: parseInt(id),
        pagar_fecha: workedMap[id] && pagarFechaMap[id]
          ? dayjs(pagarFechaMap[id]).format("YYYY-MM-DD")
          : null,
      }));

      await updateBanco(updates);

      // Refresh logic...
      const permisos = await getBancoView();
      setData(permisos);
      setFilteredData(permisos);

      const initialWorked = {};
      const initialPagarFecha = {};
      permisos.forEach((item) => {
        initialWorked[item.id] = !!item.pagar_fecha;
        initialPagarFecha[item.id] = item.pagar_fecha ? dayjs(item.pagar_fecha) : null;
      });
      setWorkedMap(initialWorked);
      setPagarFechaMap(initialPagarFecha);

      setSaveSuccess(true); // Show success modal
    } catch (error) {
      setSaveErrorMessage(error.message || "Error guardando los cambios");
      setSaveError(true); // Show error modal
    } finally {
      setIsSaving(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5 },
    { field: "id_usuario", headerName: "ID_USUARIO", flex: 0.7 },
    {
      field: "nombre",
      headerName: "NOMBRE",
      flex: 2,
      cellClassName: "name-column--cell",
    },
    {
      field: "deber_fecha",
      headerName: "Fecha Deber",
      flex: 1,
      valueFormatter: (params) => {
        if (!params.value) return "";
        return dayjs(params.value).format("DD/MM/YYYY");
      },
    },
    {
      field: "worked",
      headerName: "¿Trabajó?",
      flex: 0.7,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title={workedMap[params.row.id] ? "Trabajó en otro día" : "No ha trabajado"}>
          <Switch
            checked={!!workedMap[params.row.id]}
            onChange={(e) => handleSwitchChange(params.row, e.target.checked)}
            color="primary"
            disabled={isLoading || isSaving}
          />
        </Tooltip>
      ),
    },
    {
      field: "pagar_fecha",
      headerName: "Fecha Pagar",
      flex: 1.2,
      renderCell: (params) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={pagarFechaMap[params.row.id] || null}
            onChange={(date) => handleDateChange(params.row, date)}
            format="DD/MM/YYYY"
            disabled={!workedMap[params.row.id] || isLoading || isSaving}
            slotProps={{
              textField: {
                variant: "standard",
                sx: { minWidth: 120 },
                disabled: !workedMap[params.row.id] || isLoading || isSaving,
              },
            }}
          />
        </LocalizationProvider>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" p={0}>
        <Header title="Banco de Hora" subtitle="banco de Hora" />
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            sx={{
              background: "#143f04",
              color: "white",
              borderRadius: "8px",
              height: "3rem",
              "&:hover": { background: "#36a60f" },
            }}
            onClick={handleExport}
            variant="contained"
          >
            Exportar
          </Button>
          <Button
            sx={{
              background: "#007bff",
              color: "white",
              borderRadius: "8px",
              height: "3rem",
              "&:hover": { background: "#0056b3" },
            }}
            onClick={handleSave}
            variant="contained"
            disabled={isSaving}
          >
            {isSaving ? "Guardando..." : "Guardar"}
          </Button>
          <Box
            display="flex"
            backgroundColor={colors.primary[400]}
            borderRadius="3px"
            height={"50%"}
            width={"100%"}
            ml={1}
          >
            <InputBase
              sx={{ ml: 2, flex: 1 }}
              placeholder="Buscar por nombre"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            <IconButton type="button" sx={{ p: 1 }}>
              <SearchIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .name-column--cell": { color: colors.greenAccent[300] },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[900],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[900],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          loading={isLoading}
          rows={filteredData}
          columns={columns}
          getRowId={(row) => row.id}
          components={{ Toolbar: CustomToolbar }}
          pageSize={100}
          rowsPerPageOptions={[100]}
          autoHeight={false}
        />
      </Box>
      <ModalCharge isLoading={isLoading || isSaving} />
      <ModalSucces open={saveSuccess} onClose={() => setSaveSuccess(false)} />
      <ModalError
        open={saveError}
        onClose={() => setSaveError(false)}
        error={saveErrorMessage}
      />
    </Box>
  );
};

export default BancoView;